import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { Modal } from '@liveauctioneers/hammer-ui-core/modal';
import {
    CloseButton,
    ModalActions,
    ModalContent,
    ModalHeader,
    ModalSubTitle,
    ModalTitle,
} from '@liveauctioneers/hammer-ui-core/modal/components';
import { BodyPrimary, BodySecondary, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { useAppDispatch } from '@/redux/hooks';
import { dismissModal } from '@/redux/modules/modal';
import { getPreferencesUrl } from '@/utils/urls';

import { ExcludedHousesTable } from './ExcludedHousesTable';
import { useExcludeHouses } from './hooks/useExcludeHouses';
import { HousesToExcludeTable } from './HousesToExcludeTable';

const ExcludeHousesModal = () => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const {
        handleApplyExcludeHouses,
        handleRemoveHouse,
        handleSelectHouse,
        hasExcludedHousesChanges,
        hasHousesToExcludeChanges,
        housesToExcludeData,
        localExcludedHouses,
    } = useExcludeHouses();

    const handleModalApply = async () => {
        await handleApplyExcludeHouses();

        dispatch(dismissModal());

        const successTitle = formatMessage({ id: 'excludeAuctionHousesFilter.modal.success.title' });
        toast('success', {
            description: (
                <BodyPrimary>
                    <FormattedMessage
                        id="excludeAuctionHousesFilter.modal.success.description"
                        values={{
                            accountSettingsLink: (
                                <Link href={getPreferencesUrl()}>
                                    <FormattedMessage id="excludeAuctionHousesFilter.modal.success.accountSettingsLink" />
                                </Link>
                            ),
                        }}
                    />
                </BodyPrimary>
            ),
            title: successTitle,
        });
    };

    return (
        <Modal
            handleClose={() => dispatch(dismissModal())}
            show
            type="composable"
        >
            <CloseButton />
            <ModalHeader>
                <ModalTitle>
                    <FormattedMessage id="excludeAuctionHousesFilter.modal.title" />
                </ModalTitle>
            </ModalHeader>
            <ModalContent>
                <ModalSubTitle>
                    <FormattedMessage id="excludeAuctionHousesFilter.modal.subtitle" />
                </ModalSubTitle>
                <ExcludedHousesTable
                    className={cn(hasExcludedHousesChanges ? 'mb-2xs' : 'mb-md')}
                    houseData={localExcludedHouses}
                    onRemoveHouse={handleRemoveHouse}
                />
                {hasExcludedHousesChanges && (
                    <BodySecondary className="mb-md flex text-text-secondary">
                        <FormattedMessage id="excludeAuctionHousesFilter.modal.nonAppliedChanges" />
                    </BodySecondary>
                )}
                {housesToExcludeData.length > 0 ? (
                    <HousesToExcludeTable
                        houseData={housesToExcludeData}
                        onSelectHouse={handleSelectHouse}
                    />
                ) : (
                    <H5>
                        <FormattedMessage id="excludeAuctionHousesFilter.modal.noHousesToExclude" />
                    </H5>
                )}
            </ModalContent>
            <ModalActions>
                <div className="flex justify-end">
                    <Button
                        className="mr-sm w-[158px]"
                        onClick={() => dispatch(dismissModal())}
                        variant="outlined"
                    >
                        <FormattedMessage id="excludeAuctionHousesFilter.modal.cancelButton" />
                    </Button>
                    <Button
                        className="w-[158px]"
                        disabled={!hasExcludedHousesChanges && !hasHousesToExcludeChanges}
                        onClick={handleModalApply}
                    >
                        <FormattedMessage id="excludeAuctionHousesFilter.modal.applyButton" />
                    </Button>
                </div>
            </ModalActions>
        </Modal>
    );
};

export default ExcludeHousesModal;
